/**
 * ReportDetailView.tsx
 *
 * Unified report detail view that adapts based on user role.
 */

import { useParams, useNavigate } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { authenticatedFetch } from '@/api/client';
import ReportApp from '@/ReportApp';
import type { Issue } from '@/types/reportApp';
import { useRole } from '@/contexts/RoleContext';

export const ReportDetailView = () => {
  const { reportId } = useParams({ from: '/_auth/_shell/reports/$reportId' });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { effectiveRole } = useRole();

  const isQcView = effectiveRole === 'QC';

  // Mutation to approve report (update status to qc-reviewed)
  const approveReportMutation = useMutation({
    mutationFn: async ({ reportId }: { reportId: string; issues?: Issue[] }) => {
      console.log('Approving report:', reportId);
      const response = await authenticatedFetch(`/api/reports/${reportId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: 'qc-reviewed',
          updated_by: 'qc-team',
        }),
      });
      if (!response.ok) {
        const error = await response.json();
        console.error('Approval failed:', error);
        throw new Error(error.detail || 'Failed to approve report');
      }
      const result = await response.json();
      console.log('Approval successful:', result);
      return result;
    },
    onSuccess: async () => {
      console.log('Invalidating queries and redirecting to /reports');
      await queryClient.invalidateQueries({ queryKey: ['reports'] });
      navigate({ to: '/reports' });
    },
    onError: (error) => {
      console.error('Approval mutation error:', error);
      alert(`Failed to approve report: ${error.message}`);
    },
  });

  return (
    <ReportApp
      initialScreen="report"
      initialReportId={reportId}
      showApproveButton={isQcView}
      readOnly={!isQcView}
      onApproveReport={isQcView ? approveReportMutation.mutate : undefined}
      isApprovingReport={approveReportMutation.isPending}
      onBack={() => navigate({ to: '/reports' })}
    />
  );
};
